/* eslint-disable @next/next/no-img-element */
import React, { useContext, useEffect, useMemo, useState } from "react";
import { GetServerSideProps } from "next";
import Head from "next/head";
import { LogContext } from "context/log";
import RockstarHeader from "components/RockstarHeader";
import { META_TITLE } from "constants/meta";
import RockstarFooter from "components/RockstarFooter";
import { DatoFaqFragment, getSdk } from "services/datocms/generated";
import { createDatoClient } from "services/datocms";
import TopCtaBanner from "components/TopCtaBanner";
import { SelectDropdown } from "components/RockstarDropdown/SelectDropdown";
import { PAGE_TYPE_ENUM } from "@pepdirect/shared/types";
import { generatePageId } from "@pepdirect/helpers/analyticsLogger";
import { Faqs } from "components/Faqs";

interface FaqPageProps {
  faqContent: DatoFaqFragment[];
}

enum FAQCategories {
  GENERAL = "GENERAL",
  NUTRITIONAL = "NUTRITIONAL",
}

export const getServerSideProps: GetServerSideProps<FaqPageProps> = async (
  appContext
) => {
  try {
    const { allFaqs } = await getSdk(
      createDatoClient(appContext.preview)
    ).AllFaqs();

    return {
      props: {
        faqContent: allFaqs,
      },
    };
  } catch (error) {
    return {
      props: {
        faqContent: [],
      },
    };
  }
};

export default function FaqPage({ faqContent }: FaqPageProps): JSX.Element {
  const { useLogPageView } = useContext(LogContext);
  const [selectedFaq, setSelectedFaq] = useState(0);
  const [faqCategory, setFaqCategory] = useState<FAQCategories>(
    FAQCategories.GENERAL
  );

  useEffect(() => {
    setSelectedFaq(0);
  }, [faqCategory]);

  const onFaqClick = (index: number) => {
    setSelectedFaq(index);
  };

  const faqCategories = useMemo(
    () => [
      { value: FAQCategories.GENERAL, label: FAQCategories.GENERAL },
      { value: FAQCategories.NUTRITIONAL, label: FAQCategories.NUTRITIONAL },
    ],
    []
  );

  const title = META_TITLE;
  useLogPageView({
    pageType: PAGE_TYPE_ENUM.support,
    pageCategory: undefined,
    pageTitle: "faq",
    id: generatePageId("faq"),
  });

  const metaTitle = `${title} – ${META_TITLE}`;
  const faqCategorySelected =
    faqCategory === FAQCategories.GENERAL
      ? "General"
      : faqCategory === FAQCategories.NUTRITIONAL
      ? "Nutrition"
      : "";

  return (
    <>
      <Head>
        <title>{metaTitle}</title>
        <meta name="og:title" content={metaTitle} key="og:title" />
      </Head>

      <div id="page" className="site">
        <a className="skip-link screen-reader-text" href="#content">
          Skip to content
        </a>

        <div className="site-header-block">
          <TopCtaBanner />
          <RockstarHeader />

          <div id="content" className="site-content">
            <div id="primary" className="content-area">
              <main id="main" className="site-main" role="main">
                <article
                  id="post-87"
                  className="post-87 page type-page status-publish hentry entry"
                >
                  <header
                    className="entry-header alignwide"
                    style={{ textAlign: "center", marginTop: "24px" }}
                  >
                    <h1 className="entry-title">FAQ</h1>
                  </header>

                  <div className="faq-block" id="faq-block-block_61a0850485b6f">
                    <div className="container">
                      <div className="faq-selection">
                        <div className="faq-select">
                          <SelectDropdown
                            selectedOption={{
                              value: faqCategory,
                              label: faqCategory,
                            }}
                            onChange={async (val: string) => {
                              setFaqCategory(val as FAQCategories);
                            }}
                            options={faqCategories}
                          />
                        </div>
                      </div>
                      <Faqs
                        onFaqClick={onFaqClick}
                        selectedFaq={selectedFaq}
                        isLightBg
                        datoFaqContent={faqContent.filter(
                          (item) => item.category === faqCategorySelected
                        )}
                      />
                    </div>
                  </div>
                </article>
              </main>
            </div>
          </div>

          <RockstarFooter />
        </div>
      </div>
    </>
  );
}
